import { useCallback, useEffect, useRef, useState } from "react";

import {
  type AudibleSide,
  clampSeek,
  commonDuration,
  needsResync,
} from "./syncedPlayback";

/**
 * Drive two <video> elements as one player: one transport, one clock, one audible side.
 * `resetKey` changes whenever either source changes (new item or arm), which rewinds both.
 */
export function useSyncedPlayback(resetKey: string) {
  const left = useRef<HTMLVideoElement>(null);
  const right = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audible, setAudible] = useState<AudibleSide>("left");

  const pair = useCallback(() => {
    const leader = audible === "left" ? left.current : right.current;
    const follower = audible === "left" ? right.current : left.current;
    return { leader, follower };
  }, [audible]);

  // Mute through the DOM property: React does not keep the `muted` attribute in sync after mount.
  useEffect(() => {
    if (left.current) left.current.muted = audible !== "left";
    if (right.current) right.current.muted = audible !== "right";
  }, [audible, resetKey]);

  useEffect(() => {
    setPlaying(false);
    setTime(0);
    setDuration(0);
    for (const video of [left.current, right.current]) {
      if (!video) continue;
      video.pause();
      video.currentTime = 0;
    }
  }, [resetKey]);

  useEffect(() => {
    const videos = [left.current, right.current].filter(
      (v): v is HTMLVideoElement => v !== null,
    );
    const onMeta = () =>
      setDuration(
        commonDuration(left.current?.duration, right.current?.duration),
      );
    const onTime = () => {
      const { leader, follower } = pair();
      if (!leader) return;
      setTime(leader.currentTime);
      if (
        follower &&
        !leader.paused &&
        needsResync(leader.currentTime, follower.currentTime)
      )
        follower.currentTime = leader.currentTime;
    };
    const onEnded = () => {
      for (const video of videos) video.pause();
      setPlaying(false);
    };
    for (const video of videos) {
      video.addEventListener("loadedmetadata", onMeta);
      video.addEventListener("timeupdate", onTime);
      video.addEventListener("ended", onEnded);
    }
    onMeta();
    return () => {
      for (const video of videos) {
        video.removeEventListener("loadedmetadata", onMeta);
        video.removeEventListener("timeupdate", onTime);
        video.removeEventListener("ended", onEnded);
      }
    };
  }, [pair, resetKey]);

  const pause = useCallback(() => {
    left.current?.pause();
    right.current?.pause();
    setPlaying(false);
  }, []);

  const play = useCallback(async () => {
    const { leader, follower } = pair();
    if (!leader) return;
    if (follower) follower.currentTime = leader.currentTime;
    setPlaying(true);
    try {
      await Promise.all([leader.play(), follower?.play()]);
    } catch {
      // Autoplay policy or a load error: keep both stopped so they cannot drift apart.
      pause();
    }
  }, [pair, pause]);

  const toggle = useCallback(() => {
    if (playing) pause();
    else void play();
  }, [pause, play, playing]);

  const seek = useCallback(
    (to: number) => {
      const target = clampSeek(to, duration);
      for (const video of [left.current, right.current])
        if (video) video.currentTime = target;
      setTime(target);
    },
    [duration],
  );

  return {
    left,
    right,
    playing,
    time,
    duration,
    audible,
    setAudible,
    play,
    pause,
    toggle,
    seek,
  };
}
